import { Icon } from '@/components/ui/Icon';
import { useSession } from '@/store/session';

interface CaveBagDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CaveBagDrawer({ isOpen, onClose }: CaveBagDrawerProps) {
  const wallet = useSession((s) => s.wallet);
  const collection = useSession((s) => s.collection);

  if (!isOpen) return null;

  const totalTrash = collection?.trash_found ?? 0;
  const totalTreasure = collection?.treasure_found ?? 0;
  const score = collection?.score ?? 0;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-950/75 backdrop-blur-md animate-[fadeIn_.2s_ease-out] select-none"
      role="dialog"
      aria-modal="true"
      aria-label="In-Cave Bag & Inventory Drawer"
    >
      <div className="flex h-full w-80 flex-col border-l border-amber-500/30 bg-slate-900/95 p-5 backdrop-blur-xl shadow-2xl animate-[slideLeft_.25s_ease-out]">
        {/* Drawer Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="bag" size={20} className="text-amber-400" />
            <h2 className="font-pixel text-[11px] tracking-wider text-amber-300 uppercase drop-shadow-[0_0_8px_rgba(255,180,60,0.5)]">
              IN-CAVE BAG
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/20 bg-white/10 px-3 py-1 font-pixel text-[9px] text-white hover:bg-white/20 active:scale-95 transition-all"
          >
            CLOSE
          </button>
        </div>

        {/* Currency Balance Summary */}
        <div className="mt-4 rounded-2xl border border-amber-500/25 bg-amber-500/10 p-3.5 backdrop-blur-md">
          <p className="font-pixel text-[8px] text-amber-300/80 uppercase tracking-wider">
            WALK & DESCENT BALANCES
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-slate-950/60 p-2.5 border border-white/10">
              <span className="text-[10px] text-ink-dim block font-pixel">GOLD COINS</span>
              <span className="font-pixel text-xs text-amber-300 tabular flex items-center gap-1">
                <Icon name="coin" size={14} className="text-amber-400" />
                <span>{wallet.coins.toLocaleString()}</span>
              </span>
            </div>
            <div className="rounded-xl bg-slate-950/60 p-2.5 border border-violet-500/30">
              <span className="text-[10px] text-violet-300 block font-pixel">SHWE KYAT</span>
              <span className="font-pixel text-xs text-kyat-glow tabular flex items-center gap-1">
                <Icon name="kyat" size={14} className="text-violet-400" />
                <span>{wallet.kyat}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Collection & Loot Summary */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3.5">
          <p className="font-pixel text-[8px] text-ink-dim uppercase tracking-wider">
            COLLECTION & DISCOVERIES
          </p>
          <div className="mt-2 space-y-2 text-xs">
            <div className="flex items-center justify-between text-ink-bright">
              <span className="flex items-center gap-1.5">
                <Icon name="sparkle" size={14} className="text-amber-300" />
                <span>Treasures Discovered</span>
              </span>
              <span className="font-pixel text-[10px] text-amber-300 tabular">
                {totalTreasure}
              </span>
            </div>
            <div className="flex items-center justify-between text-ink-bright">
              <span className="flex items-center gap-1.5">
                <Icon name="rock" size={14} className="text-orange-400" />
                <span>Trash Minerals Found</span>
              </span>
              <span className="font-pixel text-[10px] text-orange-300 tabular">
                {totalTrash}
              </span>
            </div>
            <div className="flex items-center justify-between text-ink-bright">
              <span className="flex items-center gap-1.5">
                <Icon name="trophy" size={14} className="text-yellow-400" />
                <span>Total Collection Score</span>
              </span>
              <span className="font-pixel text-[10px] text-yellow-300 tabular">
                {score}
              </span>
            </div>
          </div>
        </div>

        {/* Action Tip */}
        <div className="mt-auto border-t border-white/10 pt-4">
          <p className="text-[10px] text-ink-dim leading-relaxed flex items-center gap-1.5">
            <Icon name="ad" size={14} className="text-amber-400 shrink-0" />
            <span><span className="font-semibold text-amber-200">Pro Tip:</span> Treasures carrying Shwe Kyat can be banked instantly after verified rewarded ad videos.</span>
          </p>
        </div>
      </div>
    </div>
  );
}
